/**
 * Keuze 9 van 75: "Namens Sanne" of "Namens JP" en de handtekening die daarbij
 * hoort, altijd zichtbaar onder de tekst. Niet in het bewerkbare veld: de server
 * zet hem er bij versturen onder.
 *
 * Sessie 161 (opdracht 4): het vak toont de handtekening exact zoals de klant
 * hem krijgt. Dezelfde bron en dezelfde medewerker-keuze als het versturen
 * (`GET /api/werkplek/zaken/{id}/handtekening?stem=`,
 * `werkplek_tegel.ondertekening`): bij een antwoord op een mail de medewerker
 * van de gekozen stem (Namens Sanne: Sanne, Namens JP: JP), bij een nieuwe
 * draad altijd wie verstuurt. Daar is Sanne niet te kiezen
 * (`namens_kiesbaar: false`) en toont Namens alleen JP. De HTML
 * komt alleen van de server (`signature_generator.genereer_html`) en staat in
 * een sandbox-iframe zonder scripts. Staat de vaste ondertekening van motor 6.1
 * al in de tekst, dan komt er geen tweede onder. Die ondertekening komt van de
 * server mee (`eigen_ondertekening`, herstel 161): geen kopie in de frontend.
 */
import { QueryClientContext, useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";

import { apiClient } from "@/lib/api";
import { werkplekTegelApi } from "@/lib/werkplek-api";

import { useV6Optioneel } from "../context";

/** "sanne", "jp" of wat het concept meegaf (bijvoorbeeld "nova" bij een chat
 *  die per mail beantwoord wordt): die blijft staan tot JP zelf kiest. */
export type Stem = string;

export interface Ondertekening {
  stem: string | null;
  kanaal: string;
  handtekening_html: string;
  handtekening_tekst: string;
  ondertekenaar: string | null;
  bron: string;
  reden_leeg: string | null;
  /** De vaste ondertekening van motor 6.1, van de server (herstel 161). */
  eigen_ondertekening?: string;
  /** False bij een nieuwe draad: daar ondertekent altijd wie verstuurt. */
  namens_kiesbaar?: boolean;
}

/** De handtekening bij deze zaak en stem; gedeeld door Namens en Handtekening
 *  (zelfde queryKey, dus één verzoek per stem). */
function useOndertekening(zaakId: string | null, stem: Stem) {
  return useQuery({
    queryKey: ["v6", "handtekening", "zaak", zaakId, stem],
    queryFn: () => haalOndertekening(zaakId as string, stem),
    enabled: zaakId != null,
    staleTime: 60_000,
  });
}

export function useStem(begin: string | null | undefined) {
  return useState<Stem>(begin || "sanne");
}

export function Namens({
  stem,
  onStem,
}: {
  stem: Stem;
  onStem: (s: Stem) => void;
}) {
  const zaakId = useV6Optioneel()?.zaakId ?? null;
  const client = useContext(QueryClientContext);
  // Zonder zaak of zonder query-client (losse toetsen) weet niemand of het
  // een nieuwe draad is: dan gewoon beide keuzes.
  if (zaakId == null || client == null) {
    return <NamensKeuze stem={stem} onStem={onStem} alleenJp={false} />;
  }
  return <NamensBijZaak zaakId={zaakId} stem={stem} onStem={onStem} />;
}

function NamensBijZaak({
  zaakId,
  stem,
  onStem,
}: {
  zaakId: string;
  stem: Stem;
  onStem: (s: Stem) => void;
}) {
  const { data } = useOndertekening(zaakId, stem);
  // Nieuwe draad: email_send ondertekent altijd met wie verstuurt, dus
  // "Namens Sanne" zou liegen. Alleen JP, en de stem gaat daarheen.
  const alleenJp = data?.namens_kiesbaar === false;
  useEffect(() => {
    if (alleenJp && stem !== "jp") onStem("jp");
  }, [alleenJp, stem, onStem]);
  return <NamensKeuze stem={stem} onStem={onStem} alleenJp={alleenJp} />;
}

function NamensKeuze({
  stem,
  onStem,
  alleenJp,
}: {
  stem: Stem;
  onStem: (s: Stem) => void;
  alleenJp: boolean;
}) {
  const keuzes = alleenJp ? (["jp"] as const) : (["sanne", "jp"] as const);
  return (
    <span className="m7-namens" data-testid="m7-namens">
      Namens
      <span className="seg" role="group" aria-label="Namens wie">
        {keuzes.map((s) => (
          <button
            key={s}
            type="button"
            className={stem === s ? "on" : undefined}
            aria-pressed={stem === s}
            onClick={() => onStem(s)}
          >
            {s === "jp" ? "JP" : "Sanne"}
          </button>
        ))}
      </span>
    </span>
  );
}

async function haalOndertekening(
  zaakId: string,
  stem: Stem,
): Promise<Ondertekening> {
  const { data } = await apiClient.get<Ondertekening>(
    `/api/werkplek/zaken/${zaakId}/handtekening`,
    { params: { stem, kanaal: "mail" } },
  );
  return data;
}

/** Een volledig HTML-document voor de sandbox: alleen de server-HTML. */
function document_(html: string): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    "<style>html,body{margin:0;padding:0;background:transparent;" +
    "font:12.5px/1.5 system-ui,sans-serif;color:#1f2937}</style></head>" +
    `<body>${html}</body></html>`
  );
}

function meet(frame: HTMLIFrameElement | null): number | null {
  const h = frame?.contentDocument?.body?.scrollHeight;
  return h ? h + 4 : null;
}

function HtmlHandtekening({ html }: { html: string }) {
  const [hoogte, setHoogte] = useState(140);
  const houder = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  // Herstel 161: smaller vak, hogere handtekening. Opnieuw meten als de
  // breedte van de houder verandert.
  useEffect(() => {
    const el = houder.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let breedte = el.clientWidth;
    const waarnemer = new ResizeObserver(() => {
      if (el.clientWidth === breedte) return;
      breedte = el.clientWidth;
      const h = meet(frame.current);
      if (h) setHoogte(h);
    });
    waarnemer.observe(el);
    return () => waarnemer.disconnect();
  }, []);
  return (
    <div ref={houder} data-testid="m7-handtekening-houder">
      <iframe
        ref={frame}
        title="Handtekening zoals de klant hem ziet"
        data-testid="m7-handtekening-html"
        // Geen allow-scripts: er draait niets. allow-same-origin alleen om de
        // hoogte te meten.
        sandbox="allow-same-origin"
        srcDoc={document_(html)}
        style={{ width: "100%", height: hoogte, border: 0, display: "block" }}
        onLoad={(e) => {
          const h = meet(e.currentTarget);
          if (h) setHoogte(h);
        }}
      />
    </div>
  );
}

export function Handtekening({ stem, tekst }: { stem: Stem; tekst?: string }) {
  const zaakId = useV6Optioneel()?.zaakId ?? null;
  // Sessie 161 bouwer E: de stem zit in de queryKey en in het verzoek, dus
  // bij elke wissel Sanne/JP wisselt de handtekening direct mee.
  const metZaak = useOndertekening(zaakId, stem);
  // Zonder zaak (losse toetsen van het vak): de oude route per stem.
  const perStem = useQuery({
    queryKey: ["v6", "handtekening", stem],
    queryFn: () => werkplekTegelApi.handtekening(stem),
    enabled: zaakId == null,
    staleTime: 10 * 60_000,
  });
  const { data, isError } = zaakId != null ? metZaak : perStem;

  const eigen =
    data && "eigen_ondertekening" in data
      ? data.eigen_ondertekening
      : undefined;
  if (tekst && eigen && tekst.includes(eigen)) {
    // De ondertekening staat al in de tekst; email_send plakt er geen onder.
    return <div className="m7-handtekening" data-testid="m7-handtekening" />;
  }
  const html = data?.handtekening_html?.trim();
  const reden = data && "reden_leeg" in data ? data.reden_leeg : null;
  return (
    <div className="m7-handtekening" data-testid="m7-handtekening">
      {html ? (
        <HtmlHandtekening html={html} />
      ) : isError ? (
        "Handtekening onbekend"
      ) : data ? (
        reden || "Handtekening onbekend"
      ) : (
        ""
      )}
    </div>
  );
}
